import { useSmartMutation, useSmartQuery } from "@shared/api/queries/hooks";
import { QueryKeyFactory } from "@shared/api/queries/queryKeyFactory";
import { useTenantId } from "@shared/selectors/globalSelectors";
import { getRemoteUtilityId } from "@shared/utils/getUtilityId";
import { queryClient } from "@shared/api/queries/queryClient";
import { toast } from "@shared/hooks/use-toast";
import {
  getGLCode,
  getSummary,
  getUtilityServices,
  updateGlCode,
  getChoices,
  createGlCodes,
  getGlCodes,
  getGlSources,
  getTransactionCategories,
  getTransactionTypes,
  getCsvTemplate,
  getTransactionCategoriesMultiple,
  importCsvGlCodes,
  updateGlCodeStatus,
  getGlCodeDetails,
  updateGlCodeById,
  exportGlCodes,
  getGlCodesGrouped,
  updateGlCodesMulti,
} from "./api";

export const useGlCodes = (queryParams?: Record<string, any>) => {
  return useSmartQuery(
    [QueryKeyFactory.module.bx.glCode.glCodeList(), queryParams],
    () => getGlCodes(queryParams),
  );
};

export const useGlCodesGrouped = (queryParams?: Record<string, any>) => {
  return useSmartQuery(
    [QueryKeyFactory.module.bx.glCode.glCodeList(), queryParams],
    () => getGlCodesGrouped(queryParams),
    {
      refetchOnMount: "always",
      staleTime: 0,
    }
  );
};
export const useBillDetails = (queryParams?: Record<string, any>) => {
  return useSmartQuery(
    [QueryKeyFactory.module.bx.glCode.glCodeList(), queryParams],
    () => getGLCode(queryParams),
  );
};

export const useUtilityServices = () => {
  return useSmartQuery(
    [
      QueryKeyFactory.module.bx.glCode.utilityServiceList(),
      getRemoteUtilityId(),
    ],
    () => getUtilityServices(),
    {
      staleTime: 5 * 60 * 1000, // 5 minutes
      refetchOnWindowFocus: false,
    },
  );
};

export const useUpdateGlCode = () => {
  const REMOTE_UTILITY_ID = getRemoteUtilityId();
  const tenantId = useTenantId();

  return useSmartMutation(
    async ({ entityName, item }: { entityName: string; item: any }) => {
      const today = new Date().toISOString().split("T")[0];
      const payload = {
        remote_utility_id: REMOTE_UTILITY_ID,
        remote_tenant_id: tenantId,
        configuration_json: {
          [`${entityName}`]: {
            gl_code_id: item.id,
            is_active: item.isActiveStatus,
            gl_code_value: item.configuredGlCodeValue,
            created_date: today,
          },
        },
      };

      return updateGlCode(JSON.stringify(payload));
    },
  );
};

export const useCreateGlCodes = () => {
  return useSmartMutation((payload: any) => createGlCodes(payload), {
    onSuccess: () => {
      toast({
        title: "Success",
        description: "GL Codes created successfully.",
        variant: "default",
      });
      queryClient.refetchQueries({
        queryKey: [QueryKeyFactory.module.bx.glCode.glCodeList()],
      });
      queryClient.refetchQueries({
        queryKey: [QueryKeyFactory.module.bx.glCode.summary()],
      });
    },
    onError: (error: any) => {
      toast({
        title: `Error: ${error.message || "Failed to create GL codes"}`,
        variant: "destructive",
      });
    },
  });
};

export const useUpdateGlCodesMulti = () => {
  return useSmartMutation((payload: any) => updateGlCodesMulti(payload), {
    onSuccess: () => {
      toast({
        title: "Success",
        description: "GL Codes updated successfully.",
        variant: "default",
      });
      queryClient.refetchQueries({
        queryKey: [QueryKeyFactory.module.bx.glCode.glCodeList()],
      });
      queryClient.refetchQueries({
        queryKey: [QueryKeyFactory.module.bx.glCode.summary()],
      });
    },
    onError: (error: any) => {
      toast({
        title: `Error: ${error.message || "Failed to update GL codes"}`,
        variant: "destructive",
      });
    },
  });
};

export const useUpdateGlCodeStatus = () => {
  return useSmartMutation(
    (payload: { id: string | number; status: number }) =>
      updateGlCodeStatus(payload),
    {
      onSuccess: () => {
        toast({
          title: "Success",
          description: "GL Code status updated successfully.",
          variant: "default",
        });
        queryClient.refetchQueries({
          queryKey: [QueryKeyFactory.module.bx.glCode.glCodeList()],
        });
        queryClient.refetchQueries({
          queryKey: [QueryKeyFactory.module.bx.glCode.summary()],
        });
      },
      onError: (error: any) => {
        toast({
          title: `Error: ${error.message || "Failed to update GL code status"}`,
          variant: "destructive",
        });
      },
    },
  );
};

export const useUpdateGlCodeById = () => {
  return useSmartMutation(
    (payload: { id: string | number; payload: any }) =>
      updateGlCodeById(payload),
    {
      onSuccess: () => {
        toast({
          title: "Success",
          description: "GL Code updated successfully.",
          variant: "default",
        });
        queryClient.refetchQueries({
          queryKey: [QueryKeyFactory.module.bx.glCode.glCodeList()],
        });
        queryClient.refetchQueries({
          queryKey: [QueryKeyFactory.module.bx.glCode.summary()],
        });
      },
      onError: (error: any) => {
        toast({
          title: `Error: ${error.message || "Failed to update GL code"}`,
          variant: "destructive",
        });
      },
    },
  );
};

export const useGlCodeDetails = (glCode: string | number | undefined) => {
  return useSmartQuery(
    [QueryKeyFactory.module.bx.glCode.summary(), "details", glCode],
    () => (glCode ? getGlCodeDetails(glCode) : Promise.resolve(null)),
    {
      enabled: !!glCode,
    },
  );
};

export const useSummary = () => {
  return useSmartQuery(
    [...QueryKeyFactory.module.bx.glCode.summary(), getRemoteUtilityId()],
    () => getSummary(),
    {
      staleTime: 5 * 60 * 1000, // 5 minutes
      refetchOnWindowFocus: false,
    },
  );
};

export const useChoices = (choiceType: string, options?: any) => {
  return useSmartQuery(
    [
      ...QueryKeyFactory.module.bx.glCode.choices(choiceType),
      getRemoteUtilityId(),
    ],
    () => getChoices(choiceType),
    {
      staleTime: 5 * 60 * 1000, // 5 minutes
      refetchOnWindowFocus: false,
      ...options,
    },
  );
};

export const useGlSources = (options?: any) => {
  return useSmartQuery(
    [...QueryKeyFactory.module.bx.glCode.glSources(), getRemoteUtilityId()],
    () => getGlSources(),
    {
      staleTime: 5 * 60 * 1000,
      refetchOnWindowFocus: false,
      ...options,
    },
  );
};

export const useTransactionCategories = (sources?: string, options?: any) => {
  return useSmartQuery(
    [
      ...QueryKeyFactory.module.bx.glCode.transactionCategories(sources),
      getRemoteUtilityId(),
    ],
    () => getTransactionCategories(sources),
    {
      staleTime: 5 * 60 * 1000,
      refetchOnWindowFocus: false,
      enabled: !!sources,
      ...options,
    },
  );
};

export const useTransactionCategoriesMultiple = (
  sources?: string[],
  options?: any,
) => {
  return useSmartQuery(
    [
      ...QueryKeyFactory.module.bx.glCode.transactionCategoriesMultiple(
        sources,
      ),
      getRemoteUtilityId(),
    ],
    () => getTransactionCategoriesMultiple(sources),
    {
      staleTime: 5 * 60 * 1000,
      refetchOnWindowFocus: false,
      enabled: !!sources && sources.length > 0,
      ...options,
    },
  );
};

export const useTransactionTypes = (
  transactionCategory?: string,
  options?: any,
) => {
  return useSmartQuery(
    [
      ...QueryKeyFactory.module.bx.glCode.transactionTypes(transactionCategory),
      getRemoteUtilityId(),
    ],
    () => getTransactionTypes(transactionCategory),
    {
      staleTime: 5 * 60 * 1000,
      refetchOnWindowFocus: false,
      enabled: !!transactionCategory,
      ...options,
    },
  );
};

export const useDownloadCsvTemplate = () => {
  return useSmartMutation(
    async (params?: { source?: string; transactionCategory?: string }) => {
      const blob = await getCsvTemplate(params);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "gl-codes-template.csv";
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    },
  );
};

export const useImportCsvGlCodes = () => {
  return useSmartMutation(
    async ({
      file,
      action,
      onProgress,
    }: {
      file: File;
      action: "preview" | "save";
      onProgress?: (percent: number) => void;
    }) => {
      return importCsvGlCodes(file, action, onProgress);
    },
  );
};

export const useExportGlCodes = () => {
  return useSmartMutation(async (queryParams?: Record<string, any>) => {
    const blob = await exportGlCodes(queryParams);
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `gl-codes-export-${new Date().getTime()}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);
  });
};
